/**
 * forgot-password.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Params } from "@angular/router";
import { Subscription } from "rxjs/Rx";
import "zxcvbn";

import { AlertService } from "../common/alert.service";
import { ConfigurationService } from "../configuration/configuration.service";
import { MessagingService } from "../server/messaging.service";
import { identity, oneline } from "../support/utilities/functions";
import { CustomValidators } from "../support/validators";
import { AuthService } from "./auth.service";
import { PasswordCredentials } from "./identity/credentials";

/**
 * The distinct phases of the reset process.
 */
enum ResetState {
  /**
   * Encountered an error during the process and must restart.
   */
  Failed = -1,

  /**
   * Initial state before any user action.
   */
  Start,

  /**
   * Server has acknowledged a request to send a reset email.
   */
  EmailSent,

  /**
   * The user is manually entering a token received in an email.
   */
  EnterToken,

  /**
   * The user is being presented with one or more credential challenges.
   */
  SecurePrompts,

  /**
   * The user has proven themselves and may now reset their password.
   */
  ResetForm,

  /**
   * The password reset is complete and the user will be forwarded to the app.
   */
  Complete
}

@Component({
  template: `
  <div class="container">
  <div id="loginContainer" class="well center-block">
    <form [formGroup]="form" (submit)="advance()">
      <template [ngIf]="state === ${ResetState.Start}">
        <p>
          If you've lost or forgotten your password, you can reset it here.
          Enter the email you use to login and we'll send you a link to get
          started.
        </p>
        <div class="form-group">
          <label>Enter your email:</label>
          <input
			type="text"
			autocomplete="email"
            [formControl]="login"
            autofocus
            class="form-control"
            placeholder="somebody@example.com">
        </div>
        <div class="form-group">
          <input
            type="submit"
            class="btn btn-info form-control"
            value="Request Password Reset">
        </div>
        <div class="text-center">
          <a class="text-info" (click)="state = ${ResetState.EnterToken}">
            If you already have your reset token, click here.
          </a>
        </div>
      </template>

      <template [ngIf]="state === ${ResetState.EnterToken}">
        <div class="form-group">
          <label>Enter the token sent to your email:</label>
          <input
            type="text"
            [formControl]="token"
            autofocus
            class="form-control"
            placeholder="Token...">
        </div>
        <div class="form-group">
          <input
            type="submit"
            class="btn btn-default form-control"
            value="Submit Token">
        </div>
      </template>

      <template [ngIf]="state === ${ResetState.EmailSent}">
        <p>
          If the login you entered corresponds to a registered user in the
          system, you will receive an email in the next 5 minutes with a link to
          reset your password.
        </p>
      </template>

      <template [ngIf]="state === ${ResetState.SecurePrompts}">
        <p>
          Please answer your security questions.
        </p>
        <div class="form-group">
          <label>{{securePrompt || "Loading..."}}</label>
          <input
            type="text"
            [formControl]="promptAnswer"
            autofocus
            class="form-control"
            placeholder="Answer...">
        </div>
        <div class="form-group">
          <input
            type="submit"
            class="btn btn-default form-control"
            value="Submit Answer">
        </div>
      </template>

      <template [ngIf]="state === ${ResetState.ResetForm}">
        <p>
          You have correctly answered your security questions. Please enter a
          new password below.
        </p>
        <div class="form-group">
          <label>Password:</label>
          <input
			type="password"
			autocomplete="new-password"
            [formControl]="password"
            autofocus
            class="form-control">
        </div>
        <div class="form-group">
          <password-strength-analysis
            [results]="strengthAnalysis"
            [minMagnitude]="config.protocol.minimumPasswordMagnitude">
          </password-strength-analysis>
        </div>
        <div class="form-group">
          <label>Repeat Password:</label>
          <input
            type="password"
            [formControl]="passwordDuplicate"
            class="form-control">
        </div>
        <div class="form-group">
          <input
            type="submit"
            class="btn btn-default form-control"
            value="Reset Your Password">
        </div>
      </template>
    </form>
  </div>
  </div>
  `,
  styles: [
    "#loginContainer { width: 400px; max-width: 90%;}"
  ],
})
export class ForgotPasswordComponent
{
  /**
   * The current step the user is at in the reset flow.
   */
  public state : ResetState;

  private login : FormControl;
  private token : FormControl;
  private promptAnswer : FormControl;
  private password : FormControl;
  private passwordDuplicate : FormControl;

  public securePrompt : string;

  public form : FormGroup;

  public strengthAnalysis : ZxcvbnResult;

  /**
   * Construct a new {@link LoginComponent}.
   */
  public constructor (
    private auth : AuthService,
    protected alerts : AlertService,
    protected messaging : MessagingService,
    protected route : ActivatedRoute,
    public config : ConfigurationService
  )
  {
    route.params.subscribe((params : Params) => this.start(params));
  }

  private hasToken (params : Params) : params is {token : string}
  {
    return ("token" in params) && (typeof (params as any).token === "string");
  }

  private start (params : Params) : void
  {
    this.form = new FormGroup({});

    this.login = new FormControl("", Validators.required);
    this.promptAnswer = new FormControl("", Validators.required);
    this.password = new FormControl("", Validators.required);
    this.passwordDuplicate =
      new FormControl("", CustomValidators.equalsTo(this.password));
    this.password.valueChanges.debounceTime(200).subscribe((pw : string) => {
      this.strengthAnalysis = Zxcvbn(pw);
    });

    if (this.hasToken(params))
    {
      this.state = ResetState.EnterToken;
      this.token = new FormControl(params.token, Validators.required);

      // We don't want to submit before the messaging service is active.
      this.messaging.ready.first(identity).subscribe(
        () => this.beginReset());
    }
    else
    {
      this.state = ResetState.Start;
      this.token = new FormControl("", Validators.required);
    }
  }

  public advance () : void
  {
    switch (this.state)
    {
      case ResetState.Start:
        return this.requestEmail();
      case ResetState.EnterToken:
        return this.beginReset();
      case ResetState.SecurePrompts:
        return this.submitAnswer();
			case ResetState.ResetForm:
        return this.finishReset();
      default:
        console.error(this);
        throw new Error(`Illegal state for form submission (${this.state})`);
    }
  }

  private requestEmail () : void
  {
    const email : string = this.login.value;
    this.auth.requestResetEmail(email).subscribe(
      () => {
        this.alerts.notify(`An email will be sent to ${email} shortly with a
                            link to reset your password!`);
        this.state = ResetState.EmailSent;
      }
    );
  }

  private beginReset () : void
  {
    this.auth.beginResetFlow(this.token.value as string).subscribe(
      (prompt : string) => {
        this.state = ResetState.SecurePrompts;
        this.securePrompt = prompt;
        this.promptAnswer.reset();
        this.promptAnswer.enable();
      },
      (error : any) => {
        this.state = ResetState.Failed;
        this.alerts.error((error as Error).message);
        this.auth.boot();
      },
      () => this.state = ResetState.ResetForm
    );
  }

  private submitAnswer () : void
  {
    if (!this.promptAnswer.valid)
    {
      return;
    }
    this.auth.supplySecureAnswer(this.promptAnswer.value);
    this.promptAnswer.disable();
  }

  private finishReset () : void
  {
    if (!this.password.valid || !this.passwordDuplicate.valid)
    {
      return;
		}
		if (this.strengthAnalysis.guesses_log10
			< this.config.protocol.minimumPasswordMagnitude)
		{
			alert(
        oneline`Your password is not strong enough.
								(Consider using a sequence of four or more random words.)`);
			return;
		}
    this.auth.finishResetFlow(this.password.value).subscribe(
      () => this.auth.redirectIfAuthed(),
      (error : any) => {
        console.error("Reset error:", error);
        this.alerts.error("Could not reset your password!");
      }
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/forgot-password.component.ts